"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { getB2BLanguageSwitchConfig } from "./b2bLanguageSwitch";

const LANGUAGE_METADATA: Record<string, { code: string; menuLabel: string }> = {
  en: { code: "EN", menuLabel: "English - EN" },
  hindi: { code: "HI", menuLabel: "हिंदी - HI (BETA)" },
};

const B2BLanguageSwitcher = ({ enabled = false }: { enabled?: boolean }) => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const switchConfig = useMemo(() => {
    if (!enabled || !pathname) {
      return null;
    }

    const queryString = searchParams?.toString();
    const search = queryString ? `?${queryString}` : "";
    return getB2BLanguageSwitchConfig(pathname, search);
  }, [enabled, pathname, searchParams]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  if (!switchConfig) {
    return null;
  }

  const currentOption = switchConfig.options.find(
    ({ value }) => value === switchConfig.currentLanguage
  );
  const currentLanguageLabel = currentOption?.label ?? "English";

  return (
    <div
      ref={menuRef}
      data-testid="b2b-language-switcher"
      className="relative hidden md:flex"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        aria-label="Select page language"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className="group flex cursor-pointer flex-col items-center gap-1 px-3 pt-[14px] pb-[30px] text-xs leading-[14px] font-medium text-white transition-colors hover:bg-[#001326]"
        onClick={() => setIsOpen((open) => !open)}
      >
        <i
          aria-hidden="true"
          className="bf-icon-vernac-language inline-flex h-6 w-6 items-center justify-center text-2xl leading-none"
        />
        <span className="flex items-center text-[12px] leading-[14px] whitespace-nowrap">
          <span>{currentLanguageLabel}</span>
          <i
            aria-hidden="true"
            className={`icomoon ml-[2px] text-[10px] transition-transform duration-300 before:content-['\\e981'] ${isOpen ? "-rotate-180" : "group-hover:-rotate-180"}`}
          />
        </span>
      </button>

      {isOpen ? (
        <div
          role="menu"
          aria-label="Language options"
          className="absolute top-full left-0 z-20 mt-0 min-w-[246px] overflow-hidden rounded-t-none rounded-b-2xl bg-[#03162b] py-2 text-white shadow-[0_18px_40px_rgba(0,0,0,0.35)]"
        >
          {switchConfig.options.map(({ label, value, href }) => {
            const metadata = LANGUAGE_METADATA[value] ?? {
              code: value.toUpperCase(),
              menuLabel: `${label} - ${value.toUpperCase()}`,
            };
            const isSelected = value === switchConfig.currentLanguage;

            return (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={isSelected}
                className="flex w-full cursor-pointer items-center gap-4 px-6 py-4 text-left text-[12px] leading-[14px] font-medium text-white transition-colors hover:bg-white/5"
                onClick={() => {
                  setIsOpen(false);
                  if (!isSelected) {
                    router.push(href);
                  }
                }}
              >
                <span
                  aria-hidden="true"
                  className={`inline-flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                    isSelected ? "border-[#ff7a00]" : "border-white bg-white"
                  }`}
                >
                  {isSelected ? (
                    <span className="block h-[6px] w-[6px] rounded-full bg-[#ff7a00]" />
                  ) : null}
                </span>
                <span>{metadata.menuLabel}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default B2BLanguageSwitcher;
